/**
 * Regional defaults for Kaiso System.
 *
 * BLOCO 2 — Tenant regional config structure
 * BLOCO 5 — Country defaults layer
 * BLOCO 6 — Feature flags
 *
 * USAGE:
 * - Use COUNTRY_DEFAULTS to populate defaults during initial onboarding only.
 * - NEVER apply these defaults automatically to existing restaurants.
 * - billing_currency is always EUR — do not change without SAR Stripe price_ids.
 * - Feature flags default to false — activation is always explicit, per tenant.
 *
 * IMPORTANT: Keep in sync with backend/utils/regional.py
 */

// ---------------------------------------------------------------------------
// BLOCO 3 — Currency definitions
// display_currency → shown to the customer on menu / receipt
// billing_currency → always EUR for SaaS subscription (do not change)
// ---------------------------------------------------------------------------
export const CURRENCIES = {
  EUR: { symbol: "€", name: "Euro", position: "after" },
  BRL: { symbol: "R$", name: "Real Brasileiro", position: "before" },
  USD: { symbol: "$", name: "US Dollar", position: "before" },
  GBP: { symbol: "£", name: "British Pound", position: "before" },
  // SAR — prepared for Saudi Arabia market. NOT active in billing.
  SAR: { symbol: "﷼", name: "Riyal Saudita", position: "after" },
  AED: { symbol: "AED", name: "Dirham Emiratense", position: "before" },
};

/** SaaS billing always uses EUR. Do not change without SAR Stripe price_ids. */
export const BILLING_CURRENCY = "EUR";

// ---------------------------------------------------------------------------
// BLOCO 5 — Country defaults
// Used ONLY during onboarding — never to reconfigure existing restaurants.
// ---------------------------------------------------------------------------
export const COUNTRY_DEFAULTS = {
  ES: {
    display_currency: "EUR",
    billing_currency: "EUR",
    timezone: "Europe/Madrid",
    phone_prefix: "+34",
    ui_language: "es",
    layout_direction: "ltr",
    vat_rate: 10,
    locale: "es-ES",
  },
  PT: {
    display_currency: "EUR",
    billing_currency: "EUR",
    timezone: "Europe/Lisbon",
    phone_prefix: "+351",
    ui_language: "pt",
    layout_direction: "ltr",
    vat_rate: 23,
    locale: "pt-PT",
  },
  BR: {
    display_currency: "BRL",
    billing_currency: "EUR",
    timezone: "America/Sao_Paulo",
    phone_prefix: "+55",
    ui_language: "pt",
    layout_direction: "ltr",
    vat_rate: 0,
    locale: "pt-BR",
  },
  US: {
    display_currency: "USD",
    billing_currency: "EUR",
    timezone: "America/New_York",
    phone_prefix: "+1",
    ui_language: "en",
    layout_direction: "ltr",
    vat_rate: 0,
    locale: "en-US",
  },
  GB: {
    display_currency: "GBP",
    billing_currency: "EUR",
    timezone: "Europe/London",
    phone_prefix: "+44",
    ui_language: "en",
    layout_direction: "ltr",
    vat_rate: 20,
    locale: "en-GB",
  },
  FR: {
    display_currency: "EUR",
    billing_currency: "EUR",
    timezone: "Europe/Paris",
    phone_prefix: "+33",
    ui_language: "fr",
    layout_direction: "ltr",
    vat_rate: 20,
    locale: "fr-FR",
  },
  DE: {
    display_currency: "EUR",
    billing_currency: "EUR",
    timezone: "Europe/Berlin",
    phone_prefix: "+49",
    ui_language: "de",
    layout_direction: "ltr",
    vat_rate: 19,
    locale: "de-DE",
  },
  IT: {
    display_currency: "EUR",
    billing_currency: "EUR",
    timezone: "Europe/Rome",
    phone_prefix: "+39",
    ui_language: "it",
    layout_direction: "ltr",
    vat_rate: 22,
    locale: "it-IT",
  },
  CA: {
    display_currency: "USD",
    billing_currency: "EUR",
    timezone: "America/Toronto",
    phone_prefix: "+1",
    ui_language: "en",
    layout_direction: "ltr",
    vat_rate: 0,
    locale: "en-CA",
  },
  AE: {
    display_currency: "AED",
    billing_currency: "EUR",
    timezone: "Asia/Dubai",
    phone_prefix: "+971",
    ui_language: "ar",
    layout_direction: "rtl",
    vat_rate: 5,
    locale: "ar-AE",
  },
  // Saudi Arabia — structure prepared, NOT activated in production.
  // Activation requires:
  //   - SAR Stripe recurring price_ids
  //   - Arabic locale bundle (ar translation.json with full coverage)
  //   - RTL CSS layout (rtl_enabled flag)
  //   - Mada / STC Pay payment gateway integration
  // Set per tenant via explicit PATCH with feature flags.
  SA: {
    display_currency: "SAR",
    billing_currency: "EUR",
    timezone: "Asia/Riyadh",
    phone_prefix: "+966",
    ui_language: "ar",
    layout_direction: "rtl",
    vat_rate: 15,
    locale: "ar-SA",
  },
};

/**
 * Return defaults for a country code. Falls back to ES.
 * Use ONLY during onboarding — never to reconfigure existing restaurants.
 */
export function getCountryDefaults(countryCode) {
  return COUNTRY_DEFAULTS[countryCode?.toUpperCase()] ?? COUNTRY_DEFAULTS.ES;
}

// ---------------------------------------------------------------------------
// BLOCO 7 — Onboarding wizard i18n strings
// ---------------------------------------------------------------------------
const _ONBOARDING_STRINGS = {
  pt: {
    steps: [
      { title: "Teu Restaurante", desc: "Dados básicos do negócio" },
      { title: "Logo e Marca", desc: "Identidade visual" },
      { title: "Tipo de Restaurante", desc: "Template com categorias e produtos" },
      { title: "Cores do Menu", desc: "Personalizar aparência" },
      { title: "Horários", desc: "Quando está aberto" },
      { title: "Pagamentos", desc: "Como receber pagamentos" },
      { title: "Delivery", desc: "Entregas e retirada" },
      { title: "Fidelidade", desc: "Programa de pontos" },
      { title: "Pronto!", desc: "Tudo configurado" },
    ],
    days: { lunes: "Segunda", martes: "Terça", miercoles: "Quarta", jueves: "Quinta", viernes: "Sexta", sabado: "Sábado", domingo: "Domingo" },
    labels: {
      market: "Mercado", restaurantName: "Nome do restaurante", address: "Endereço completo",
      phone: "Telefone", next: "Próximo", back: "Voltar", skip: "Pular", finish: "Finalizar",
      done: "Ir para o Menu", step: "de", saving: "Salvando...",
      errorRequired: "Todos os campos são obrigatórios", errorSave: "Erro ao salvar. Tente novamente.",
      logoUploaded: "Logo carregado com sucesso!", logoRound: "Redondo", logoSquare: "Quadrado",
      templateApplied: "Template aplicado!", applyToAll: "Aplicar a todos", addSlot: "turno",
      cashLabel: "Dinheiro", cashDesc: "Pagamento em espécie no local",
      cardLabel: "Cartão (Stripe)", cardDesc: "Visa, Mastercard via Stripe",
      bizumDesc: "Pagamento instantâneo (Espanha)", mbwayDesc: "Pagamento instantâneo (Portugal)",
      paymentHint: "Para configurar Stripe ou Bizum, vá em Ajustes > Pagamentos depois do onboarding.",
      pickupLabel: "Retirada no local", pickupDesc: "Cliente busca o pedido no restaurante",
      deliveryLabel: "Delivery", deliveryDesc: "Entrega no endereço do cliente",
      shippingFee: "Taxa de entrega padrão", shippingHint: "Pode configurar zonas depois em Ajustes > Envios.",
      bothDisabledTitle: "Pedidos online desativados", bothDisabledDesc: "O menu público não receberá pedidos online.",
      loyaltyLabel: "Programa de Fidelidade", loyaltyDesc: "Clientes ganham pontos a cada compra",
      ptsPerEuro: "Pontos por unidade gasta", doneTitle: "Restaurante configurado!",
      doneDesc: "Agora adicione suas categorias e produtos no menu.",
      publicUrlLabel: "Seu link público (compartilhe com clientes)",
      copyLink: "Copiar", openLink: "Abrir", aiHelp: "Ajuda IA", howItWorks: "Como funciona",
    },
  },
  es: {
    steps: [
      { title: "Tu Restaurante", desc: "Datos básicos del negocio" },
      { title: "Logo y Marca", desc: "Identidad visual" },
      { title: "Tipo de Restaurante", desc: "Plantilla con categorías y productos" },
      { title: "Colores del Menú", desc: "Personalizar apariencia" },
      { title: "Horarios", desc: "Cuándo estás abierto" },
      { title: "Pagos", desc: "Cómo recibir pagos" },
      { title: "Delivery", desc: "Entregas y recogida" },
      { title: "Fidelidad", desc: "Programa de puntos" },
      { title: "¡Listo!", desc: "Todo configurado" },
    ],
    days: { lunes: "Lunes", martes: "Martes", miercoles: "Miércoles", jueves: "Jueves", viernes: "Viernes", sabado: "Sábado", domingo: "Domingo" },
    labels: {
      market: "Mercado", restaurantName: "Nombre del restaurante", address: "Dirección completa",
      phone: "Teléfono", next: "Siguiente", back: "Volver", skip: "Omitir", finish: "Finalizar",
      done: "Ir al Menú", step: "de", saving: "Guardando...",
      errorRequired: "Todos los campos son obligatorios", errorSave: "Error al guardar. Inténtalo de nuevo.",
      logoUploaded: "¡Logo cargado con éxito!", logoRound: "Redondo", logoSquare: "Cuadrado",
      templateApplied: "¡Plantilla aplicada!", applyToAll: "Aplicar a todos", addSlot: "turno",
      cashLabel: "Efectivo", cashDesc: "Pago en efectivo en el local",
      cardLabel: "Tarjeta (Stripe)", cardDesc: "Visa, Mastercard via Stripe",
      bizumDesc: "Pago instantáneo (España)", mbwayDesc: "Pago instantáneo (Portugal)",
      paymentHint: "Para configurar Stripe o Bizum, ve a Ajustes > Pagos después del onboarding.",
      pickupLabel: "Recogida en local", pickupDesc: "El cliente recoge el pedido en el restaurante",
      deliveryLabel: "Delivery", deliveryDesc: "Entrega en la dirección del cliente",
      shippingFee: "Tarifa de envío por defecto", shippingHint: "Puedes configurar zonas en Ajustes > Envíos.",
      bothDisabledTitle: "Pedidos online desactivados", bothDisabledDesc: "El menú público no recibirá pedidos online.",
      loyaltyLabel: "Programa de Fidelidad", loyaltyDesc: "Los clientes ganan puntos por cada compra",
      ptsPerEuro: "Puntos por unidad gastada", doneTitle: "¡Restaurante configurado!",
      doneDesc: "Ahora añade tus categorías y productos al menú.",
      publicUrlLabel: "Tu enlace público (comparte con clientes)",
      copyLink: "Copiar", openLink: "Abrir", aiHelp: "Ayuda IA", howItWorks: "Cómo funciona",
    },
  },
  en: {
    steps: [
      { title: "Your Restaurant", desc: "Basic business info" },
      { title: "Logo & Brand", desc: "Visual identity" },
      { title: "Restaurant Type", desc: "Template with categories & items" },
      { title: "Menu Colors", desc: "Customize appearance" },
      { title: "Hours", desc: "When you're open" },
      { title: "Payments", desc: "How to receive payments" },
      { title: "Delivery", desc: "Deliveries & pickup" },
      { title: "Loyalty", desc: "Points program" },
      { title: "Done!", desc: "All set" },
    ],
    days: { lunes: "Monday", martes: "Tuesday", miercoles: "Wednesday", jueves: "Thursday", viernes: "Friday", sabado: "Saturday", domingo: "Sunday" },
    labels: {
      market: "Market", restaurantName: "Restaurant name", address: "Full address",
      phone: "Phone", next: "Next", back: "Back", skip: "Skip", finish: "Finish",
      done: "Go to Menu", step: "of", saving: "Saving...",
      errorRequired: "All fields are required", errorSave: "Error saving. Please try again.",
      logoUploaded: "Logo uploaded successfully!", logoRound: "Round", logoSquare: "Square",
      templateApplied: "Template applied!", applyToAll: "Apply to all", addSlot: "shift",
      cashLabel: "Cash", cashDesc: "Payment in cash on-site",
      cardLabel: "Card (Stripe)", cardDesc: "Visa, Mastercard via Stripe",
      bizumDesc: "Instant payment (Spain)", mbwayDesc: "Instant payment (Portugal)",
      paymentHint: "To configure Stripe, go to Settings > Payments after onboarding.",
      pickupLabel: "Pickup", pickupDesc: "Customer picks up the order at the restaurant",
      deliveryLabel: "Delivery", deliveryDesc: "Deliver to the customer's address",
      shippingFee: "Default shipping fee", shippingHint: "Configure delivery zones later in Settings > Shipping.",
      bothDisabledTitle: "Online orders disabled", bothDisabledDesc: "The public menu will not receive online orders.",
      loyaltyLabel: "Loyalty Program", loyaltyDesc: "Customers earn points per purchase",
      ptsPerEuro: "Points per unit spent", doneTitle: "Restaurant configured!",
      doneDesc: "Now add your real categories and products to the menu.",
      publicUrlLabel: "Your public link (share with customers)",
      copyLink: "Copy", openLink: "Open", aiHelp: "AI Help", howItWorks: "How it works",
    },
  },
  fr: {
    steps: [
      { title: "Votre Restaurant", desc: "Informations de base" },
      { title: "Logo & Marque", desc: "Identité visuelle" },
      { title: "Type de Restaurant", desc: "Modèle avec catégories" },
      { title: "Couleurs du Menu", desc: "Personnaliser l'apparence" },
      { title: "Horaires", desc: "Quand vous êtes ouvert" },
      { title: "Paiements", desc: "Comment recevoir les paiements" },
      { title: "Livraison", desc: "Livraisons et retrait" },
      { title: "Fidélité", desc: "Programme de points" },
      { title: "Terminé !", desc: "Tout configuré" },
    ],
    days: { lunes: "Lundi", martes: "Mardi", miercoles: "Mercredi", jueves: "Jeudi", viernes: "Vendredi", sabado: "Samedi", domingo: "Dimanche" },
    labels: {
      market: "Marché", restaurantName: "Nom du restaurant", address: "Adresse complète",
      phone: "Téléphone", next: "Suivant", back: "Retour", skip: "Passer", finish: "Terminer",
      done: "Aller au Menu", step: "sur", saving: "Enregistrement...",
      errorRequired: "Tous les champs sont obligatoires", errorSave: "Erreur lors de l'enregistrement. Réessayez.",
      logoUploaded: "Logo téléchargé avec succès !", logoRound: "Rond", logoSquare: "Carré",
      templateApplied: "Modèle appliqué !", applyToAll: "Appliquer à tous", addSlot: "service",
      cashLabel: "Espèces", cashDesc: "Paiement en espèces sur place",
      cardLabel: "Carte (Stripe)", cardDesc: "Visa, Mastercard via Stripe",
      bizumDesc: "Paiement instantané (Espagne)", mbwayDesc: "Paiement instantané (Portugal)",
      paymentHint: "Pour configurer Stripe, allez dans Paramètres > Paiements après l'onboarding.",
      pickupLabel: "Retrait en magasin", pickupDesc: "Le client récupère la commande au restaurant",
      deliveryLabel: "Livraison", deliveryDesc: "Livraison à l'adresse du client",
      shippingFee: "Frais de livraison par défaut", shippingHint: "Configurez les zones dans Paramètres > Livraisons.",
      bothDisabledTitle: "Commandes en ligne désactivées", bothDisabledDesc: "Le menu public ne recevra pas de commandes en ligne.",
      loyaltyLabel: "Programme de Fidélité", loyaltyDesc: "Les clients gagnent des points à chaque achat",
      ptsPerEuro: "Points par EUR dépensé", doneTitle: "Restaurant configuré !",
      doneDesc: "Ajoutez maintenant vos vraies catégories et produits.",
      publicUrlLabel: "Votre lien public (partagez avec vos clients)",
      copyLink: "Copier", openLink: "Ouvrir", aiHelp: "Aide IA", howItWorks: "Comment ça marche",
    },
  },
  de: {
    steps: [
      { title: "Ihr Restaurant", desc: "Grundlegende Informationen" },
      { title: "Logo & Marke", desc: "Visuelle Identität" },
      { title: "Restauranttyp", desc: "Vorlage mit Kategorien" },
      { title: "Menüfarben", desc: "Aussehen anpassen" },
      { title: "Öffnungszeiten", desc: "Wann Sie geöffnet sind" },
      { title: "Zahlungen", desc: "Wie Sie Zahlungen erhalten" },
      { title: "Lieferung", desc: "Lieferungen & Abholung" },
      { title: "Treueprogramm", desc: "Punkteprogramm" },
      { title: "Fertig!", desc: "Alles eingerichtet" },
    ],
    days: { lunes: "Montag", martes: "Dienstag", miercoles: "Mittwoch", jueves: "Donnerstag", viernes: "Freitag", sabado: "Samstag", domingo: "Sonntag" },
    labels: {
      market: "Markt", restaurantName: "Restaurantname", address: "Vollständige Adresse",
      phone: "Telefon", next: "Weiter", back: "Zurück", skip: "Überspringen", finish: "Fertigstellen",
      done: "Zum Menü", step: "von", saving: "Speichern...",
      errorRequired: "Alle Felder sind erforderlich", errorSave: "Fehler beim Speichern. Bitte erneut versuchen.",
      logoUploaded: "Logo erfolgreich hochgeladen!", logoRound: "Rund", logoSquare: "Quadratisch",
      templateApplied: "Vorlage angewendet!", applyToAll: "Auf alle anwenden", addSlot: "Schicht",
      cashLabel: "Bargeld", cashDesc: "Barzahlung vor Ort",
      cardLabel: "Karte (Stripe)", cardDesc: "Visa, Mastercard über Stripe",
      bizumDesc: "Sofortzahlung (Spanien)", mbwayDesc: "Sofortzahlung (Portugal)",
      paymentHint: "Um Stripe-Schlüssel zu konfigurieren, gehen Sie zu Einstellungen > Zahlungen.",
      pickupLabel: "Abholung", pickupDesc: "Kunde holt die Bestellung im Restaurant ab",
      deliveryLabel: "Lieferung", deliveryDesc: "Lieferung an die Kundenadresse",
      shippingFee: "Standard-Liefergebühr", shippingHint: "Lieferzonen in Einstellungen > Versand konfigurieren.",
      bothDisabledTitle: "Online-Bestellungen deaktiviert", bothDisabledDesc: "Das öffentliche Menü erhält keine Online-Bestellungen.",
      loyaltyLabel: "Treueprogramm", loyaltyDesc: "Kunden sammeln Punkte bei jedem Kauf",
      ptsPerEuro: "Punkte pro ausgegebenem EUR", doneTitle: "Restaurant konfiguriert!",
      doneDesc: "Fügen Sie jetzt Ihre echten Kategorien und Produkte hinzu.",
      publicUrlLabel: "Ihr öffentlicher Link (mit Kunden teilen)",
      copyLink: "Kopieren", openLink: "Öffnen", aiHelp: "KI-Hilfe", howItWorks: "So funktioniert es",
    },
  },
  it: {
    steps: [
      { title: "Il tuo Ristorante", desc: "Informazioni di base" },
      { title: "Logo & Marchio", desc: "Identità visiva" },
      { title: "Tipo di Ristorante", desc: "Modello con categorie e prodotti" },
      { title: "Colori del Menu", desc: "Personalizza l'aspetto" },
      { title: "Orari", desc: "Quando sei aperto" },
      { title: "Pagamenti", desc: "Come ricevere i pagamenti" },
      { title: "Consegna", desc: "Consegne e ritiro" },
      { title: "Fedeltà", desc: "Programma punti" },
      { title: "Fatto!", desc: "Tutto configurato" },
    ],
    days: { lunes: "Lunedì", martes: "Martedì", miercoles: "Mercoledì", jueves: "Giovedì", viernes: "Venerdì", sabado: "Sabato", domingo: "Domenica" },
    labels: {
      market: "Mercato", restaurantName: "Nome del ristorante", address: "Indirizzo completo",
      phone: "Telefono", next: "Avanti", back: "Indietro", skip: "Salta", finish: "Fine",
      done: "Vai al Menu", step: "di", saving: "Salvataggio...",
      errorRequired: "Tutti i campi sono obbligatori", errorSave: "Errore nel salvataggio. Riprova.",
      logoUploaded: "Logo caricato con successo!", logoRound: "Rotondo", logoSquare: "Quadrato",
      templateApplied: "Modello applicato!", applyToAll: "Applica a tutti", addSlot: "turno",
      cashLabel: "Contanti", cashDesc: "Pagamento in contanti in loco",
      cardLabel: "Carta (Stripe)", cardDesc: "Visa, Mastercard via Stripe",
      bizumDesc: "Pagamento istantaneo (Spagna)", mbwayDesc: "Pagamento istantaneo (Portogallo)",
      paymentHint: "Per configurare Stripe, vai a Impostazioni > Pagamenti dopo l'onboarding.",
      pickupLabel: "Ritiro in negozio", pickupDesc: "Il cliente ritira l'ordine al ristorante",
      deliveryLabel: "Consegna", deliveryDesc: "Consegna all'indirizzo del cliente",
      shippingFee: "Tariffa di consegna predefinita", shippingHint: "Configura le zone in Impostazioni > Spedizioni.",
      bothDisabledTitle: "Ordini online disattivati", bothDisabledDesc: "Il menu pubblico non riceverà ordini online.",
      loyaltyLabel: "Programma Fedeltà", loyaltyDesc: "I clienti guadagnano punti ad ogni acquisto",
      ptsPerEuro: "Punti per EUR speso", doneTitle: "Ristorante configurato!",
      doneDesc: "Ora aggiungi le tue categorie e prodotti reali al menu.",
      publicUrlLabel: "Il tuo link pubblico (condividi con i clienti)",
      copyLink: "Copia", openLink: "Apri", aiHelp: "Aiuto IA", howItWorks: "Come funziona",
    },
  },
};

/** Returns onboarding strings for the given language code. Falls back to Spanish. */
export function getOnboardingStrings(lang) {
  return _ONBOARDING_STRINGS[lang] ?? _ONBOARDING_STRINGS.es;
}

// ---------------------------------------------------------------------------
// BLOCO 8 — Onboarding checklist i18n strings
// ---------------------------------------------------------------------------
const _CHECKLIST_STRINGS = {
  pt: {
    title: "Checklist de Ativação", readyTitle: "Pronto para ativar!",
    setupNow: "Configurar Agora", activate: "Ativar Restaurante", activating: "Ativando...",
    activationSuccess: "Restaurante ativado com sucesso!", loading: "Verificando configuração...",
    operatingReady: "Restaurante pronto para operar!", completeSteps: "Complete estes passos para ativar o menu público.",
  },
  es: {
    title: "Checklist de Activación", readyTitle: "¡Listo para activar!",
    setupNow: "Configurar Ahora", activate: "Activar Restaurante", activating: "Activando...",
    activationSuccess: "¡Restaurante activado con éxito!", loading: "Verificando configuración...",
    operatingReady: "¡Restaurante listo para operar!", completeSteps: "Completa estos pasos para activar el menú público.",
  },
  en: {
    title: "Activation Checklist", readyTitle: "Ready to activate!",
    setupNow: "Configure Now", activate: "Activate Restaurant", activating: "Activating...",
    activationSuccess: "Restaurant activated successfully!", loading: "Checking configuration...",
    operatingReady: "Restaurant ready to operate!", completeSteps: "Complete these steps to activate the public menu.",
  },
  fr: {
    title: "Liste de vérification", readyTitle: "Prêt à activer !",
    setupNow: "Configurer maintenant", activate: "Activer le restaurant", activating: "Activation...",
    activationSuccess: "Restaurant activé avec succès !", loading: "Vérification de la configuration...",
    operatingReady: "Restaurant prêt à opérer !", completeSteps: "Complétez ces étapes pour activer le menu public.",
  },
  de: {
    title: "Aktivierungs-Checkliste", readyTitle: "Bereit zur Aktivierung!",
    setupNow: "Jetzt konfigurieren", activate: "Restaurant aktivieren", activating: "Aktivierung...",
    activationSuccess: "Restaurant erfolgreich aktiviert!", loading: "Konfiguration wird überprüft...",
    operatingReady: "Restaurant bereit für den Betrieb!", completeSteps: "Schließen Sie diese Schritte ab, um das öffentliche Menü zu aktivieren.",
  },
  it: {
    title: "Lista di controllo", readyTitle: "Pronto per l'attivazione!",
    setupNow: "Configura ora", activate: "Attiva ristorante", activating: "Attivazione...",
    activationSuccess: "Ristorante attivato con successo!", loading: "Verifica della configurazione...",
    operatingReady: "Ristorante pronto per operare!", completeSteps: "Completa questi passaggi per attivare il menu pubblico.",
  },
};

/** Returns checklist strings for the given language code. Falls back to Spanish. */
export function getChecklistStrings(lang) {
  return _CHECKLIST_STRINGS[lang] ?? _CHECKLIST_STRINGS.es;
}

// ---------------------------------------------------------------------------
// BLOCO 6 — Feature flags
// All false by default. Set explicitly per restaurant via PATCH /restaurant.
// ---------------------------------------------------------------------------
export const FEATURE_FLAGS_TEMPLATE = {
  /** Allow display_currency != EUR in public-facing UI (menu/cart display only) */
  multi_currency_enabled: false,
  /** Activate coherence guard + gateway validation for non-EUR checkout.
   *  Blocked automatically if no gateway supports display_currency. */
  regional_checkout_enabled: false,
  /** Enable RTL layout rendering (required for Arabic, Hebrew, etc.) */
  rtl_enabled: false,
  /** Enable Arabic UI language (requires ar translation bundle) */
  arabic_enabled: false,
  /** Enable market-specific payment methods (Mada, STC Pay, MB Way, etc.) */
  local_payment_methods_enabled: false,
};

/**
 * Fields that require an explicit feature flag before being honoured.
 * When the flag is false, fall back to default behaviour regardless of field value.
 */
export const GATED_FIELDS = {
  layout_direction: "rtl_enabled",  // "rtl" value only active when rtl_enabled=true
  ui_language: "arabic_enabled",    // "ar" value only active when arabic_enabled=true
};
